import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import OTPInput from 'react-otp-input';
import {
  browserName,
  browserVersion,
  isMobile,
  mobileModel,
  mobileVendor,
  osName,
  osVersion,
} from 'react-device-detect';

import { StepperTab } from './StepperTab';
import { InitStore, useInitStore } from 'stores/useInitStore';
import { AxiosError } from 'interfaces/interfaces';
import { NotificationsStore, useNotificationsStore } from 'stores/useNotificationsStore';
import authService from 'services/authService';
import { LOCALIZATION } from 'constants/localization';
import Notification from 'components/common/Notification';

export const AccountVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialSeconds = 60;
  const [passcode, setPasscode] = useState<string>('');
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(initialSeconds);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [disableVerifyPassCode, setDisableVerifyPassCode] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string>(location.state.statusMessage);

  const { setAppLoading } = useInitStore((state: InitStore) => ({
    setAppLoading: state.setAppLoading,
  }));

  const { processMessages, notificationsData, setInterScreenNotification, resetMessages } = useNotificationsStore(
    (state: NotificationsStore) => ({
      processMessages: state.processMessages,
      notificationsData: state.notificationsData,
      setInterScreenNotification: state.setInterScreenNotification,
      resetMessages: state.resetMessages,
    }),
  );

  useEffect(() => {
    let interval = 0;
    if (isActive && seconds > 0) {
      interval = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1);
      }, 1000);
    } else if (!isActive && seconds !== 0) {
      clearInterval(interval);
    } else if (seconds === 0) {
      clearInterval(interval);
      setIsActive(false);
      setDisableVerifyPassCode(false);
    }
    return () => clearInterval(interval);
  }, [isActive, seconds]);

  useEffect(() => {
    if (notificationsData?.interScreenNotification?.length) {
      processMessages(notificationsData?.interScreenNotification);
      setInterScreenNotification([]);
    }
  }, [notificationsData?.interScreenNotification, processMessages, setInterScreenNotification]);

  const handleVerify = async () => {
    resetMessages();
    try {
      const ipResponse = await authService.getPublicIP();
      setAppLoading(true);
      const params = {
        passCode: passcode,
        planNumber: location.state.planNumber,
        accountNumber: location.state.accountNumber,
        firstName: location.state.firstName,
        lastName: location.state.lastName,
        email: location.state.email,
        phoneNumber: location.state.phoneNumber,
        zipcode: location.state.zipcode,
        personNumber: location.state.personNumber,
        clientLoginIp: ipResponse.data?.ip,
        userDeviceInfo: {
          userOS: osName,
          userOSVersion: osVersion,
          browserName: browserName,
          browserVersion: browserVersion,
          isMobile: isMobile ? 'Y' : 'N',
          mobileModel: mobileModel,
          mobileVendor: mobileVendor,
        },
      };

      const response = await authService.accountVerification(params);
      if (response.data.verified) {
        setIsVerified(response.data.verified);
        // setInterScreenNotification(response.data.messages);
        // handleNext();
      } else if (response.data.disableVerifyPassCode) {
        setDisableVerifyPassCode(true);
        setSeconds(response.data.passCodeCoolDownPeriod);
        setIsActive(true);
      }
      processMessages(response.data.messages);
    } catch (error) {
      console.error(error);
      const axiosError = error as AxiosError;
      processMessages(axiosError?.response?.data?.messages);
    } finally {
      setAppLoading(false);
    }
  };

  // const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (e.key === 'Enter') {
  //     handleVerify();
  //   }
  // };

  const handleNext = () => {
    navigate('/sign-in-creds', {
      state: {
        ...location.state,
      },
    });
  };

  const resetTimer = () => {
    setSeconds(initialSeconds);
    setIsActive(true);
  };

  const handleResend = async () => {
    if (seconds <= 0) {
      resetMessages();
      try {
        setAppLoading(true);
        const ipResponse = await authService.getPublicIP();
        const params = {
          hostName: document.location.hostname,
          planNumber: location.state.planNumber as string,
          accountNumber: location.state.accountNumber as string,
          firstName: location.state.firstName as string,
          lastName: location.state.lastName as string,
          email: location.state.email as string,
          client: location.state.client,
          clientName: location.state.clientName,
          personNumber: location.state.personNumber as string,
          phase: 'registration',
          clientLoginIp: ipResponse.data?.ip,
          userDeviceInfo: {
            userOS: osName,
            userOSVersion: osVersion,
            browserName: browserName,
            browserVersion: browserVersion,
            isMobile: isMobile ? 'Y' : 'N',
            mobileModel: mobileModel,
            mobileVendor: mobileVendor,
          },
        };

        const response = await authService.resendPasscode(params);
        if (response.status === 200) {
          resetTimer();
          setStatusMessage(response.data.statusMessage);
        } else {
          processMessages(response.data.messages);
        }
      } catch (error) {
        console.error(error);
        const axiosError = error as AxiosError;
        processMessages(axiosError?.response?.data?.messages);
      } finally {
        setAppLoading(false);
      }
    }
  };

  return (
    <>
      <Helmet>{/* <title>{state.pageTitle}</title> */}</Helmet>
      <div className="registerWrapper">
        <div className="registerHeader">
          <h1 className="mainHeadingT">{LOCALIZATION.EN.REGISTRATION_ALL_STEPS}</h1>
          {!!notificationsData.nonInfoNotificaiton.length &&
            notificationsData.nonInfoNotificaiton.map((notification, index) => (
              <Notification key={index} id={index} message={notification} />
            ))}
        </div>
        <StepperTab step={3} />
        <div className="usernameWrapper">
          <div className="lusernameColumn mobileButton">
            <div className="mt-3 mb-3 mobileView">
              <strong>Step 3: </strong> Account Verification
            </div>
            <div className="backgroundSetColor">
              <div className="formLayout ">
                <div className="form-group">
                  <h5>One Time Passcode (OTP)</h5>
                  <span>{statusMessage}</span>
                </div>
                <div className="form-group">
                  <div className="passcode">
                    <label htmlFor="passcode">Enter your OTP</label>
                    <div className="d-flex align-items-center">
                      <OTPInput
                        value={passcode}
                        onChange={setPasscode}
                        numInputs={6}
                        renderSeparator={<span>-</span>}
                        renderInput={(props) => <input {...props} style={{ width: '30px', textAlign: 'center' }} />}
                        inputType="number"
                      />
                      {isVerified && (
                        <span className="material-icons" style={{ marginLeft: '8px', color: '#198754' }}>
                          check_circle
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <button
                    className={`btn btn-primary agiaBlueButton ${(passcode.length !== 6 || disableVerifyPassCode) && 'disabled'}`}
                    onClick={handleVerify}
                  >
                    Confirm Code
                  </button>
                </div>
                <div>
                  <span>
                    Didn&apos;t work?{' '}
                    <button
                      className={`btn btn-link`}
                      style={{ textDecoration: 'none', color: '#3399cc', padding: '0px 0px 4px' }}
                      onClick={handleResend}
                    >
                      Request another code
                    </button>{' '}
                    in{' '}
                    <span>
                      {Math.floor(seconds / 60) < 10 ? '0' : ''}
                      {Math.floor(seconds / 60)}:{seconds % 60 < 10 ? '0' : ''}
                      {seconds % 60}
                    </span>
                  </span>
                  <br />
                  <span>If it still does not arrive, please check your SPAM / Junk folder.</span>
                  <br />
                  <span>Need to change or confirm your email?</span>
                  <br />
                  <span>Click the Back button to return to Step 2.</span>
                </div>
              </div>
            </div>
            <div className="roundBottomButton">
              <Link
                to={{
                  pathname: '/personal-info',
                }}
                state={location.state}
              >
                Back
              </Link>
              <button
                id="ma-ra-step3"
                className={`btn btn-primary agiaBlueButton ${!isVerified && 'disabled'}`}
                onClick={handleNext}
              >
                Next: Login Information
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountVerification;
